import React, { createContext, useCallback, useMemo, useRef, useState } from 'react'
import { useOverlay } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { useTranslation } from 'react-i18next'
import uuid from '../utils/uuid'
import useSessionStats from '../hooks/useSessionStats'
import { getConclusions, putConclusions } from '../pages/verify-places/api'
import useCache from '../hooks/useCache'

export const VerifyPlacesContext = createContext({})

const noop = () => {}

export const VerifyPlacesProvider = ({ children }) => {
  const [inputText, setInputText] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [mode, setMode] = useState('edit') // view, edit, add
  const [params, setParams] = useState({})
  const [searchCriteria, setSearchCriteria] = useState({})
  const [step, setStep] = useState({ x: 1, y: 10 })
  const [steps, setSteps] = useState([])
  const scrollToPerson = useRef(noop)
  const scrollToCongrats = useRef(noop)
  const verifyPlacesRef = useRef(null)
  const sessionStorageKey = 'verifyPlacesSession'
  const congratulationsDialogOverlay = useOverlay()
  const lostSessionDialogOverlay = useOverlay()
  const { cisId } = useUser()
  const { t } = useTranslation()
  const { deleteStats, updateStats, stats } = useSessionStats()
  const { clear, getItem, setItem } = useCache(sessionStorageKey)

  const setCheckoutData = useCallback(
    async (data) => {
      if (data?.length === 0) {
        setStep({ x: 1, y: 0 })
        setSteps([])
        return
      }

      const batch = data.map((stp) => ({
        conclusionId: stp.conclusionId,
        date: stp.date,
        factType: t(
          `places.event-type.${(stp?.type || '').replace(/.*\//i, '')?.toUpperCase()}`,
          (stp?.type || '').replace(/.*\//i, '')
        ),
        id: stp.id || uuid(),
        otherPlaceFactValues: (stp.otherPlaceFactValues || []).map((fact) => ({
          id: fact?.id || uuid(),
          type: t(
            `places.event-type.${(fact?.type || '').replace(/.*\//i, '')?.toUpperCase()}`,
            (fact?.type || '').replace(/.*\//i, '')
          ),
          date: fact?.date?.normalized?.[0]?.value,
          placeText: fact?.place?.names?.[0]?.value || fact?.place?.original,
        })),
        personId: stp.personId,
        placeText: stp.placeText,
        related: stp.related,
        relationshipDescription: stp.relationshipDescription,
        type: t(
          `places.event-type.${(stp?.factType || stp?.type || '').replace(/.*\//i, '').toUpperCase()}`,
          (stp?.factType || stp?.type || '').replace(/.*\//i, '')
        ),
        userDefinedType: stp.userDefinedType,
      }))
      setItem({ batch, searchCriteria, currentStep: 0 })
      // sessionStorage.setItem(sessionStorageKey, serialize({ batch, searchCriteria, currentStep: 0 }))
      setSteps(batch)
      setStep({ x: 1, y: batch.length })
    },
    [t, searchCriteria, setItem]
  )

  /**
   * Saves the current batch of verified places to the server.
   * This function is called when the user has completed verifying all the places in the current batch.
   * It retrieves the batch data from session storage, maps it to the required conclusion format, and sends it to the server using the `putConclusions` API.
   * After the data is successfully saved, the session storage key is removed to clear the batch data.
   *
   * @returns {Promise<void>} A Promise that resolves when the batch has been successfully saved.
   */
  const saveBatch = useCallback(async () => {
    // get the batch saved in storage because if we are dealing with a browser back button or tab or window closure
    // the component will be unmounted and the steps will be empty
    try {
      const { batch } = await getItem()
      // console.log({ batch })
      if (batch?.length) {
        if (!cisId) throw new Error("Can't save conclusions without a CIS ID")
        const conclusions = batch.map((stp) => ({
          cisId,
          personId: stp?.personId,
          conclusionId: stp?.conclusionId,
          placeRepId: stp?.savedData?.placeRepId,
          origNonStdPlace: stp?.placeText,
          timeStamp: Date.now(),
          type: stp?.selectedType || null,
        }))
        updateStats({
          ...stats,
          verifyPlaces: {
            prev: stats?.verifyPlaces?.prev || 0,
            cur: (stats?.verifyPlaces?.cur || 0) + conclusions.filter((stp) => stp.type === 'SAVE_PLACE').length,
          },
        })
        await putConclusions({ conclusions })
      }
    } catch (err) {
      console.error('VerifyPlacesProvider::saveBatch error', err)
    } finally {
      clear()
    }
  }, [cisId, clear, getItem, stats, updateStats])

  /**
   * Handles the logic for navigating to the previous step in the verify places workflow.
   * If the current step is not the first step, it updates the current step and scrolls to the person.
   * If the current step is the first step, it does nothing.
   *
   * @returns {void}
   */
  const previousHandler = useCallback(async () => {
    setStep((cur) => {
      if (cur.x > 1) {
        const prev = { ...cur, x: cur.x - 1 }
        scrollToPerson.current()
        return prev
      }
      setInputText('')
      return cur
    })
  }, [])

  /**
   * Handles the logic for navigating to the next step in the verify places workflow.
   * If the current step is not the last step, it updates the current step and scrolls to the person.
   * If the current step is the last step, it triggers the congratulations dialog overlay.
   *
   * @returns {void}
   */
  const nextHandler = useCallback(async () => {
    setStep((cur) => {
      if (cur.x < cur.y) {
        const nextStep = { ...cur, x: cur.x + 1 }
        scrollToPerson.current()
        setInputText('')
        return nextStep
      }
      const nextStep = { ...cur, x: cur.x }
      congratulationsDialogOverlay.handleClick()
      return nextStep
    })
  }, [congratulationsDialogOverlay])

  // This returns the user to the opportunities page when they are done verifying places
  // This is used to scroll verify places into view when the user returns from the verify places app
  // It also removes the current batch from storage
  const backHandler = useCallback(() => {
    // user is leaving so we clear the current batch from storage
    saveBatch()
    sessionStorage.setItem('verifyPlaces', 'false')
    congratulationsDialogOverlay.close()
    setInputText('')
    setLoaded(false)
  }, [saveBatch, congratulationsDialogOverlay])

  /**
   * Updates the step data in the steps array, including the selected type and saved data for a specific step.
   * The updated steps array is then saved to session storage.
   *
   * @param {string} param0.id - The ID of the step to update.
   * @param {string} param0.selectedType - The new selected type for the step.
   * @param {any} param0.savedData - The new saved data for the step.
   */
  const updateStepData = useCallback(
    ({ id, selectedType, savedData }) => {
      setSteps((prevBatch) => {
        const batch = prevBatch.map((stp) => ({
          ...stp,
          ...(stp.id === id && {
            selectedType,
            savedData,
            selectedPlace: savedData,
          }),
        }))
        batch.currentStep = batch.findIndex((sp) => sp.id === id)
        // console.log('VerifyPlacesProvider::updateStepData::batch', batch)
        setItem({ batch, searchCriteria, currentStep: batch.findIndex((sp) => sp.id === id) + 1 })
        return batch
      })
    },
    [searchCriteria, setItem]
  )

  /**
   * Asynchronously searches for places based on the provided search criteria.
   *
   * @param {Object} searchCriteria - The search criteria to use for the place search.
   * @param {number} searchCriteria.batchSize - The number of places to retrieve per search.
   * @param {string} searchCriteria.country - The country ID to use for the search.
   * @param {string} [searchCriteria.lastName] - The last name to use for the search.
   * @returns {Promise<void>} - A promise that resolves when the search is complete.
   */
  const searchPlaces = useCallback(
    async (source, vpLoggingFF) => {
      try {
        const resp = await getConclusions({
          count: searchCriteria?.batchSize,
          countryRepId: searchCriteria.country,
          ...(searchCriteria?.lastName && { surname: searchCriteria.lastName }),
          source,
          vpLoggingFF,
        })
        const { data } = resp
        setCheckoutData(data)
      } catch (err) {
        // eat the error - we can't save because we don't have data to save
        console.error('searchPlaces error', err)
      }
    },
    [searchCriteria, setCheckoutData]
  )

  const value = useMemo(
    () => ({
      backHandler,
      clearCache: clear,
      congratulationsDialogOverlay,
      deleteStats,
      getCacheItem: getItem,
      inputText,
      loaded,
      lostSessionDialogOverlay,
      mode,
      nextHandler,
      params,
      previousHandler,
      saveBatch,
      scrollToCongrats,
      scrollToPerson,
      searchCriteria,
      searchPlaces,
      sessionStorageKey,
      setCacheItem: setItem,
      setCheckoutData,
      setInputText,
      setLoaded,
      setMode,
      setParams,
      setSearchCriteria,
      setStep,
      setSteps,
      stats,
      step,
      steps,
      updateStepData,
      verifyPlacesRef,
    }),
    [
      backHandler,
      clear,
      congratulationsDialogOverlay,
      deleteStats,
      getItem,
      inputText,
      loaded,
      lostSessionDialogOverlay,
      mode,
      nextHandler,
      params,
      previousHandler,
      saveBatch,
      searchCriteria,
      searchPlaces,
      setCheckoutData,
      setItem,
      stats,
      step,
      steps,
      updateStepData,
    ]
  )

  // console.log('steps', steps)
  // console.log('step', step)
  return <VerifyPlacesContext.Provider value={value}>{children}</VerifyPlacesContext.Provider>
}
